import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Section, ErrorPanel, LoadingRow, EmptyNote, UserLink, KV } from '../components/ui/bits';
import { useSel, queryClient } from '../lib/queries';
import { supabase } from '../lib/supabase';
import { adminApi } from '../lib/adminApi';
import { fmtDateTime, fmtBytes } from '../lib/format';

/** Renders one report jsonb without assuming its exact shape. */
const ReportView: React.FC<{ report: unknown }> = ({ report }) => {
  if (!report || typeof report !== 'object') return <EmptyNote>No report stored</EmptyNote>;
  const obj = report as Record<string, unknown>;
  return (
    <div className="space-y-3">
      {Object.entries(obj).map(([k, v]) => (
        <div key={k}>
          <h3 className="text-[12px] font-bold text-stone-700 mb-1">{k.replace(/_/g, ' ')}</h3>
          {typeof v === 'string' || typeof v === 'number' ? (
            <p className="text-[13px] text-stone-700 leading-relaxed">{String(v)}</p>
          ) : Array.isArray(v) ? (
            <div className="space-y-2">
              {v.map((item, i) => (
                typeof item === 'object' && item !== null ? (
                  <div key={i} className="p-2.5 rounded-md bg-stone-50 border border-stone-100">
                    {Object.entries(item as Record<string, unknown>).map(([ik, iv]) => (
                      <p key={ik} className="text-[12px] text-stone-700 mb-1 last:mb-0">
                        <span className="font-semibold text-stone-500">{ik.replace(/_/g, ' ')}: </span>
                        {typeof iv === 'object' ? JSON.stringify(iv) : String(iv)}
                      </p>
                    ))}
                  </div>
                ) : <p key={i} className="text-[12.5px] text-stone-700">{String(item)}</p>
              ))}
            </div>
          ) : (
            <pre className="text-[11px] bg-stone-50 rounded-md p-2 overflow-x-auto text-stone-600">{JSON.stringify(v, null, 2)}</pre>
          )}
        </div>
      ))}
    </div>
  );
};

export const SessionDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const [confirm, setConfirm] = useState<'rescore' | 'retranscribe' | 'revoke' | null>(null);
  const [busyNote, setBusyNote] = useState<string | null>(null);
  const [playUrl, setPlayUrl] = useState<string | null>(null);

  const { data: rows, error, isLoading } = useSel<any[]>(['session', id], () =>
    supabase.from('sessions').select('*').eq('id', id).limit(1));
  const s = rows?.[0];

  const { data: recs } = useSel<any[]>(['session', id, 'recs'], () =>
    supabase.from('session_recordings')
      .select('question_index, storage_path, audio_storage_path, size_bytes, duration_ms, mime_type, session_id, recording_session_id')
      .or(s?.recording_session_id ? `session_id.eq.${id},recording_session_id.eq.${s.recording_session_id}` : `session_id.eq.${id}`)
      .order('question_index'),
    { enabled: !!s });

  if (isLoading) return <LoadingRow label="Loading session" />;
  if (error) return <ErrorPanel error={error} />;
  if (!s) return <ErrorPanel error="Session not found" />;

  const transcript: any[] = Array.isArray(s.transcript) ? s.transcript : [];

  const play = async (path: string) => {
    const { data: signed } = await supabase.storage.from('session-recordings').createSignedUrl(path, 900);
    setPlayUrl(signed?.signedUrl ?? null);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['session', id] });

  return (
    <div>
      <Link to="/sessions" className="inline-flex items-center gap-1 text-[12px] text-stone-400 hover:text-stone-700 mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> Sessions
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-bold text-stone-900">{s.title || s.type}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-1 text-[12px] text-stone-500">
            <Badge tone={s.status === 'completed' ? 'emerald' : s.status === 'failed' ? 'rose' : 'amber'}>{s.status}</Badge>
            <span>{s.type}</span>
            {s.overall_score != null && <span>· score {s.overall_score}</span>}
            {s.duration_seconds != null && <span>· {Math.round(s.duration_seconds / 60)} min</span>}
            <span>· {fmtDateTime(s.created_at)}</span>
            <span>· <UserLink id={s.user_id} /></span>
            {s.share_token && <Badge tone="indigo">Publicly shared</Badge>}
          </div>
        </div>
        <div className="flex gap-1.5">
          {transcript.length > 0 && <Button size="sm" onClick={() => setConfirm('rescore')}>Regenerate report</Button>}
          {(recs ?? []).length > 0 && <Button size="sm" onClick={() => setConfirm('retranscribe')}>Re-transcribe and score</Button>}
          {s.share_token && <Button size="sm" variant="danger" onClick={() => setConfirm('revoke')}>Revoke share</Button>}
        </div>
      </div>

      {busyNote && <div className="mb-3 p-2.5 rounded-md bg-indigo-50 border border-indigo-200 text-[12px] text-indigo-700">{busyNote}</div>}

      {s.status === 'failed' && (
        <div className="mb-4 p-3 rounded-lg bg-rose-50 border border-rose-200">
          <p className="text-[13px] font-bold text-rose-700 mb-0.5">Failed</p>
          <p className="text-[12px] text-rose-600">What the user was told: {s.failure_reason ?? 'nothing recorded'}</p>
          <p className="text-[11px] text-rose-500 mt-1">The raw cause, if captured, is on the failures view under Sessions.</p>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-3">
        <div className="md:col-span-2 space-y-3">
          <Section title="Report">
            <ReportView report={s.report} />
          </Section>
          <Section title="Transcript" sub={`${transcript.length} answer${transcript.length === 1 ? '' : 's'}`}>
            {transcript.length === 0 ? <EmptyNote>No transcript stored</EmptyNote> : (
              <div className="space-y-3">
                {transcript.map((t, i) => (
                  <div key={i}>
                    <p className="text-[12px] font-semibold text-stone-800 mb-1">{t.question ?? `Question ${i + 1}`}</p>
                    <p className="text-[12.5px] text-stone-600 leading-relaxed whitespace-pre-wrap">{t.answer ?? String(t)}</p>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
        <div className="space-y-3">
          <Section title="Recordings">
            {(recs ?? []).length === 0 ? <EmptyNote>No recordings</EmptyNote> : (
              <div className="space-y-1.5">
                {(recs ?? []).map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-[12px]">
                    <span className="text-stone-700">Question {Number(r.question_index) + 1}
                      <span className="text-stone-400"> · {fmtBytes(r.size_bytes)}{r.session_id ? '' : ' · unlinked'}</span>
                    </span>
                    <Button size="sm" onClick={() => play(r.storage_path)}>Play</Button>
                  </div>
                ))}
                {playUrl && <video controls src={playUrl} className="mt-2 w-full rounded-md border border-stone-200" />}
              </div>
            )}
          </Section>
          <Section title="Details">
            <dl>
              <KV k="Session id">{s.id}</KV>
              <KV k="Recording run">{s.recording_session_id ?? 'none'}</KV>
              <KV k="Completed">{fmtDateTime(s.completed_at)}</KV>
              <KV k="Shared at">{fmtDateTime(s.shared_at)}</KV>
            </dl>
            <details className="mt-2">
              <summary className="text-[11px] text-stone-400 cursor-pointer">Raw config</summary>
              <pre className="text-[10.5px] bg-stone-50 rounded-md p-2 overflow-x-auto text-stone-600 mt-1">{JSON.stringify(s.config, null, 2)}</pre>
            </details>
          </Section>
        </div>
      </div>

      <ConfirmDialog
        open={confirm === 'rescore'} onClose={() => setConfirm(null)}
        title="Regenerate this report" confirmLabel="Regenerate"
        body={<>Runs feedback generation again from the stored transcript through the admin repair path, then marks the session completed. One Gemini call.</>}
        onConfirm={async () => {
          setBusyNote('Regenerating, this takes up to a minute');
          try { await adminApi('regenerate_report', { sessionId: id }); setBusyNote('Report regenerated'); invalidate(); }
          catch (e) { setBusyNote(null); throw e; }
        }}
      />
      <ConfirmDialog
        open={confirm === 'retranscribe'} onClose={() => setConfirm(null)}
        title="Re-transcribe and score" confirmLabel="Run repair"
        body={<>Sends each stored clip back through Whisper (audio siblings preferred), rebuilds the transcript, then regenerates the report. Takes a minute or two.</>}
        onConfirm={async () => {
          setBusyNote('Re-transcribing clips, then scoring');
          try {
            const r = await adminApi<{ transcribed: number; failures: number[] }>('retranscribe_session', { sessionId: id });
            setBusyNote(`Transcribed ${r.transcribed} clips${r.failures?.length ? `, ${r.failures.length} failed` : ''}, report regenerated`);
            invalidate();
          } catch (e) { setBusyNote(null); throw e; }
        }}
      />
      <ConfirmDialog
        open={confirm === 'revoke'} onClose={() => setConfirm(null)}
        title="Revoke the public share link" tone="danger" confirmLabel="Revoke"
        body={<>The share URL stops resolving immediately. The report stays intact for the user.</>}
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_revoke_share', { p_session_id: id });
          if (e) throw new Error(e.message);
          invalidate();
        }}
      />
    </div>
  );
};
